import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { clsx } from 'clsx'
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isToday, getDay, addMonths, subMonths } from 'date-fns'
import { zhCN } from 'date-fns/locale'

interface DatePickerProps {
  value?: string  // ISO 日期字符串，如 "2026-01-28"
  onChange: (date: string | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

// 快捷选项
const QUICK_OPTIONS = [
  { label: '今天', getDays: () => 0 },
  { label: '明天', getDays: () => 1 },
  { label: '后天', getDays: () => 2 },
  { label: '本周五', getDays: () => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    // 周日=0, 周一=1, ..., 周五=5, 周六=6
    const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 7 - dayOfWeek + 5
    return daysUntilFriday === 0 ? 7 : daysUntilFriday // 如果今天是周五，选下周五
  }},
  { label: '下周一', getDays: () => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek
    return daysUntilMonday
  }},
]

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六']

export default function DatePicker({
  value,
  onChange,
  placeholder = '选择日期',
  disabled = false,
  className,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      return new Date(value)
    }
    return new Date()
  })
  const containerRef = useRef<HTMLDivElement>(null)

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 获取当月日期网格
  const getDaysInMonth = () => {
    const start = startOfMonth(viewDate)
    const end = endOfMonth(viewDate)
    const days = eachDayOfInterval({ start, end })
    
    // 补齐月初空白
    const startDayOfWeek = getDay(start)
    const prefixDays = Array(startDayOfWeek).fill(null)
    
    return [...prefixDays, ...days]
  }

  const selectedDate = value ? new Date(value) : null

  const handleSelectDate = (date: Date) => {
    onChange(format(date, 'yyyy-MM-dd'))
    setIsOpen(false)
  }

  const handleQuickSelect = (daysToAdd: number) => {
    const date = addDays(new Date(), daysToAdd)
    onChange(format(date, 'yyyy-MM-dd'))
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(undefined)
    setIsOpen(false)
  }

  const formatDisplayDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return format(date, 'M月d日 (E)', { locale: zhCN })
  }

  return (
    <div ref={containerRef} className={clsx('relative inline-block', className)}>
      {/* 触发按钮 */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors',
          disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300',
          value ? 'text-gray-800' : 'text-gray-500'
        )}
      >
        <Calendar className="w-4 h-4" />
        <span>{value ? formatDisplayDate(value) : placeholder}</span>
        {value && !disabled && (
          <X
            className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600"
            onClick={handleClear}
          />
        )}
      </button>

      {/* 弹出日历 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4 min-w-[280px]"
          >
            {/* 快捷选项 */}
            <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-gray-100">
              {QUICK_OPTIONS.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => handleQuickSelect(option.getDays())}
                  className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-primary-100 hover:text-primary-700 rounded-full transition-colors"
                >
                  {option.label}
                </button>
              ))}
              {value && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-2.5 py-1 text-xs bg-red-50 text-red-600 hover:bg-red-100 rounded-full transition-colors"
                >
                  清除
                </button>
              )}
            </div>

            {/* 月份导航 */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => setViewDate(subMonths(viewDate, 1))}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <span className="font-medium text-gray-800">
                {format(viewDate, 'yyyy年M月', { locale: zhCN })}
              </span>
              <button
                type="button"
                onClick={() => setViewDate(addMonths(viewDate, 1))}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* 星期表头 */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs text-gray-400 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* 日期网格 */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="w-8 h-8" />
                }
                
                const isSelected = selectedDate && isSameDay(day, selectedDate)
                const isTodayDate = isToday(day)
                const isCurrentMonth = isSameMonth(day, viewDate)
                
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={() => handleSelectDate(day)}
                    className={clsx(
                      'w-8 h-8 rounded-lg text-sm transition-colors',
                      isSelected
                        ? 'bg-primary-600 text-white'
                        : isTodayDate
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : isCurrentMonth
                            ? 'hover:bg-gray-100 text-gray-700'
                            : 'text-gray-300'
                    )}
                  >
                    {format(day, 'd')}
                  </button>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * 内联日期选择按钮（用于紧凑布局）
 */
interface InlineDateButtonProps {
  value?: string
  onChange: (date: string | undefined) => void
  label?: string
  className?: string
}

export function InlineDateButton({
  value,
  onChange,
  label = '设置日期',
  className,
}: InlineDateButtonProps) {
  return (
    <DatePicker
      value={value}
      onChange={onChange}
      placeholder={label}
      className={className}
    />
  )
}
